import React, { useState, useEffect, useContext, useCallback } from 'react';
import { Outlet, useNavigate, useLocation, useMatch } from 'react-router-dom';

import eventBus from '../../../utils/eventBus';
import timeFast from '../../../assets/production-flow/time-fast.svg';
import movement from '../../../assets/stock/TableMovementHeader/refresh-active.svg';
import pcp from '../../../assets/production-flow/pcp.svg';
import filter from '../../../assets/stock/TableMovementHeader/filter.svg';
import { LeftSide, SectionName, SubHeaderContainer, TabsDiv, Tab, RightSide } from '../../../components/styles/subheader-style';
import { FilterDiv, FilterImg, FilterText, Content, Select, NewOPButton } from './style';
import ModalContext from '../../../contexts/ModalContext';
import ModuleKanban from '../Pipelines/ModuleKanban';
import { Filter } from 'lucide-react';
import styled from 'styled-components';

type PCPViewType = 'table' | 'board';

const PRODUCTION_FASHION_BASE = '/panel/production-fashion';

export interface ProductionFashionOutletType {
  tabIndex: number;
  setTabIndex: React.Dispatch<React.SetStateAction<number>>;
  pcpView: PCPViewType;
  setPCPView: React.Dispatch<React.SetStateAction<PCPViewType>>;
  quantity: number;
  setQuantity: React.Dispatch<React.SetStateAction<number>>;
  refresh: boolean;
  setRefresh: React.Dispatch<React.SetStateAction<boolean>>;
}

const TAB_INDEX_ORDEM_PRODUCAO = 0;
const TAB_INDEX_MOVEMENT = 1;
const TAB_INDEX_PCP = 2;
const TAB_INDEX_BOARD = 3;

const ContentStartFashion = styled(Content)`
background-color: #f4f4f4;
overflow: auto;
`;

const tabs = [
  { path: PRODUCTION_FASHION_BASE, image: timeFast, name: 'Ordem de produção', index: TAB_INDEX_ORDEM_PRODUCAO },
  { path: `${PRODUCTION_FASHION_BASE}/movement`, image: movement, name: 'Movimentação', index: TAB_INDEX_MOVEMENT },
  { path: `${PRODUCTION_FASHION_BASE}/pcp`, image: pcp, name: 'PCP', index: TAB_INDEX_PCP },
  { path: `${PRODUCTION_FASHION_BASE}/board`, image: movement, name: 'Funil', index: TAB_INDEX_BOARD }
];

const ProductionFashionSubHeader: React.FC<{
  tabIndexActived: number;
  pcpView: PCPViewType;
  setPCPView: React.Dispatch<React.SetStateAction<PCPViewType>>;
  onAddOPClick: () => void;
}> = ({ tabIndexActived, pcpView, setPCPView, onAddOPClick }) => {
const navigate = useNavigate();

const isOrdemProducaoTab = tabIndexActived === TAB_INDEX_ORDEM_PRODUCAO;
const isMovementTab = tabIndexActived === TAB_INDEX_MOVEMENT;
const isPCPTab = tabIndexActived === TAB_INDEX_PCP;
const isBoardTab = tabIndexActived === TAB_INDEX_BOARD;
const { setModalActive, setModalSection } = useContext(ModalContext)

const addFashionOP = () => {
  setModalActive(true)
  setModalSection('addFashionOP')
}

return (
  <SubHeaderContainer>
    <LeftSide>
      <SectionName>
        Produção
      </SectionName>
      <TabsDiv>
        {tabs.map((tab, index) => (
          <Tab
            key={index}
            $isFirst={index === 0}
            $isLast={index === tabs.length - 1}
            $isActive={index === tabIndexActived}
            onClick={() => {
              if (index !== tabIndexActived) navigate(tab.path);
            }}
          >
            {index === TAB_INDEX_BOARD ? (
              <Filter size={12.8} style={{ marginRight: 7 }} />
            ) : (
              <img src={tab.image} alt={`${tab.path}-icon`} />
            )}
            {tab.name}
          </Tab>
        ))}
      </TabsDiv>
    </LeftSide>
    <RightSide>
      {(isOrdemProducaoTab || isMovementTab) && (
        <FilterDiv>
          <FilterImg src={filter} alt='filter-icon' />
          <FilterText>Filtrar</FilterText>
        </FilterDiv>
      )}

      {isPCPTab && (
        <>
          <span>Visualizar por</span>
          <Select
            value={pcpView}
            onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setPCPView(e.target.value as PCPViewType)}
          >
            <option value="table">Tabela</option>
            <option value="board">Quadro</option>
          </Select>

          <NewOPButton onClick={onAddOPClick}>
            + Adicionar OP
          </NewOPButton>
        </>
      )}
    </RightSide>
  </SubHeaderContainer>
);
};

const ProductionFashionContent = () => {
const location = useLocation();
const navigate = useNavigate();
const startMatch = useMatch('/panel/production-fashion/start/:orderId');
const boardMatch = useMatch('/panel/production-fashion/board');
const [tabIndex, setTabIndex] = useState<number>(TAB_INDEX_ORDEM_PRODUCAO);
const [pcpView, setPCPView] = useState<PCPViewType>('table');
const [quantity, setQuantity] = useState(50);
const [refresh, setRefresh] = useState(false);

useEffect(() => {
  const bumpMovements = () => setRefresh((r) => !r);
  eventBus.on('refreshProductionTable', bumpMovements);
  return () => {
    eventBus.off('refreshProductionTable', bumpMovements);
  };
}, []);

const handleAddOPClick = useCallback(() => {
  navigate('/panel/production-fashion/pcp/create');
}, [navigate]);

useEffect(() => {
  const p = location.pathname;
  if (p.includes('/production-fashion/start/')) {
    return;
  }
  let next = TAB_INDEX_ORDEM_PRODUCAO;
  if (p.includes('/production-fashion/board')) {
    next = TAB_INDEX_BOARD;
  } else if (p.includes('/production-fashion/pcp')) {
    next = TAB_INDEX_PCP;
  } else if (p.includes('/production-fashion/movement')) {
    next = TAB_INDEX_MOVEMENT;
  } else if (/\/production-fashion\/?$/.test(p)) {
    next = TAB_INDEX_ORDEM_PRODUCAO;
  }
  setTabIndex(next);
}, [location.pathname]);

const outletContext: ProductionFashionOutletType = {
  tabIndex,
  setTabIndex,
  pcpView,
  setPCPView,
  quantity,
  setQuantity,
  refresh,
  setRefresh,
};

if (startMatch) {
  return (
    <ContentStartFashion>
      <Outlet context={outletContext} />
    </ContentStartFashion>
  );
}

if (boardMatch) {
  return (
    <>
      <ModuleKanban
        moduleType="production"
        moduleName="Produção"
        subModule="fashion"
        headerLeftContent={
          <>
            <SectionName>Produção</SectionName>
            <TabsDiv>
              {tabs.map((tab, index) => (
                <Tab
                  key={index}
                  $isFirst={index === 0}
                  $isLast={index === tabs.length - 1}
                  $isActive={index === tabIndex}
                  onClick={() => {
                    if (index !== tabIndex) navigate(tab.path);
                  }}
                >
                  {index === TAB_INDEX_BOARD ? (
                    <Filter size={12.8} style={{ marginRight: 7 }} />
                  ) : (
                    <img src={tab.image} alt={`${tab.path}-icon`} />
                  )}
                  {tab.name}
                </Tab>
              ))}
            </TabsDiv>
          </>
        }
      />
    </>
  );
}

return (
  <>
    <ProductionFashionSubHeader
      tabIndexActived={tabIndex}
      pcpView={pcpView}
      setPCPView={setPCPView}
      onAddOPClick={handleAddOPClick}
    />
    <Content>
      <Outlet context={outletContext} />
    </Content>
  </>
);
};

export default ProductionFashionContent;